<script setup lang="ts">
import { computed, ref } from "vue";
import type { LogEvent } from "@/types/protocol";
import { formatDateTime } from "@/utils/format";

const props = defineProps<{ events: readonly LogEvent[] }>();

const filter = ref("");

const shown = computed(() => {
  const needle = filter.value.trim().toLowerCase();
  if (!needle) return props.events;
  return props.events.filter((e) =>
    [e.machine, e.kind, e.user, e.detail].some((field) => field?.toLowerCase().includes(needle)),
  );
});

/** Ereignisarten farblich gruppieren (gut/warnend/Kommando). */
function toneOf(kind: string): string {
  if (/^(SessionLogon|SessionUnlock|Connected|SystemBoot|SystemResume)$/.test(kind)) return "good";
  if (/^(SessionLogoff|SessionLock|Disconnected|SystemShutdown|SystemSuspend)$/.test(kind)) return "warn";
  if (kind.startsWith("Command")) return "accent";
  return "";
}
</script>

<template>
  <section>
    <div class="head">
      <h2>Ereignis-Log</h2>
      <input v-model="filter" type="text" placeholder="filtern (PC, Benutzer, Ereignis) …" />
    </div>

    <div class="panel wrap">
      <table>
        <thead>
          <tr>
            <th style="width: 180px">Zeit</th>
            <th style="width: 150px">PC</th>
            <th style="width: 150px">Ereignis</th>
            <th style="width: 160px">Benutzer</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(event, index) in shown" :key="`${event.timestamp}-${index}`">
            <td><time>{{ formatDateTime(event.timestamp) }}</time></td>
            <td>{{ event.machine }}</td>
            <td class="kind" :class="toneOf(event.kind)">{{ event.kind }}</td>
            <td>{{ event.user ?? "" }}</td>
            <td>{{ event.detail ?? "" }}</td>
          </tr>
          <tr v-if="!shown.length">
            <td colspan="5" class="none">
              {{ events.length ? "Kein Treffer." : "Noch keine Ereignisse." }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped>
.head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.head input {
  min-width: 240px;
  margin-bottom: 10px;
}

.wrap {
  overflow: auto;
  max-height: 460px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}

th,
td {
  text-align: left;
  padding: 7px 10px;
  border-bottom: 1px solid var(--line);
}

th {
  color: var(--muted);
  font-weight: 500;
  position: sticky;
  top: 0;
  background: var(--panel);
}

time {
  color: var(--muted);
  white-space: nowrap;
}

.kind {
  font-weight: 600;
}

.kind.good {
  color: var(--ok);
}

.kind.warn {
  color: var(--warn);
}

.kind.accent {
  color: var(--accent);
}

.none {
  color: var(--muted);
  text-align: center;
  padding: 16px;
}
</style>
